type JsonValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | JsonValue[]
  | { [key: string]: JsonValue };

const transformKeys = (input: JsonValue, transform: (key: string) => string): JsonValue => {
  if (Array.isArray(input)) {
    return input.map((item) => transformKeys(item, transform));
  }
  if (input !== null && typeof input === 'object') {
    return Object.keys(input).reduce<Record<string, JsonValue>>((acc, key) => {
      acc[transform(key)] = transformKeys(input[key], transform);
      return acc;
    }, {});
  }
  return input;
};

/**
 * Recursively rename all object keys from `snake_case` to `camelCase`. Arrays and
 * primitives pass through untouched. Used to normalize backend API responses before
 * they hit the Redux store.
 */
export const snakeToCamel = <T = unknown>(obj: unknown): T =>
  transformKeys(obj as JsonValue, (key) =>
    key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase()),
  ) as T;

/**
 * Recursively rename all object keys from `camelCase` to `snake_case`. Inverse of
 * `snakeToCamel`; handles arrays correctly (previous implementation flattened them
 * into objects with numeric keys).
 */
export const camelToSnake = <T = unknown>(obj: unknown): T =>
  transformKeys(obj as JsonValue, (key) =>
    key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`),
  ) as T;
